import { ReactElement, useCallback } from "react";
import { useRenderManager } from "../../useRenderManager";
import { OneByOneTileContent } from "../OneByOneTileContent";
import { TwoByTwoTileContent } from "../TwoByTwoTileContent";
import { AppType, DestinationWindow } from "client/Api/Apps/common";
import { navigateToToDoApp } from "client_legacy/Apps/appNavigation";
import { useRegisterTileUsage } from "../useRegisterTileUsage";
import { ToDoListIdentifier } from "client/Api/Workspace/LiveInformation/ToDos/toDoListLiveInformation";
import { navigateToToDoList } from "./navigateToToDoList";
import { ToDoLiveTileContent } from "./ToDoLiveTileContent";
import { ToDoLiveInformationProvider } from "./provider";
import { TileRenderState } from "../../RenderState/common";
import { useBaseNavigation } from "../useInvokeNavigation";
import { TileContextProvider } from "../../TileContextProvider";
import { Coordinate } from "client/Misc/commonTypes";

export type ToDoTileContentProps = {
	renderState: TileRenderState<AppType.ToDo>;
	onEdit: (() => void) | undefined;
	onOpenTileMenu: (coordinate: Coordinate) => void;
	provider: ToDoLiveInformationProvider;
};

export const ToDoTileContent = (props: ToDoTileContentProps): ReactElement => {
	const {
		renderState,
		onEdit,
		onOpenTileMenu,
		provider
	} = props;

	const [navigationElement, navigateHandler] = useToDoTileNavigation(renderState, onEdit);

	const {
		dimension,
		data: { settings }
	} = renderState;

	const liveTileNavigateHandler = useCallback(
		(list?: ToDoListIdentifier | undefined): void => {
			if (list) {
				navigateToToDoList(
					list,
					{
						openInNewWindow: settings.destinationWindow === DestinationWindow.NewWindow
					}
				);
			} else {
				navigateHandler();
			}

		},
		[navigateHandler, settings.destinationWindow]
	);

	const tileContent: ReactElement = useRenderManager(
		dimension,
		() => (
			<ToDoLiveTileContent
				destinationWindow={settings.destinationWindow}
				provider={provider}
				onNavigate={liveTileNavigateHandler}
				onOpenTileMenu={onOpenTileMenu}
			/>
		),
		[
			{ columnSpan: 1, rowSpan: 1 },
			() => (
				<OneByOneTileContent
					onClick={navigateHandler}
					onOpenTileMenu={onOpenTileMenu}
				/>
			)
		],
		[
			{ columnSpan: 2, rowSpan: 2 },
			() => (
				<TwoByTwoTileContent
					onClick={navigateHandler}
					onOpenTileMenu={onOpenTileMenu}
				/>
			)
		]
	);

	return (
		<>
			<TileContextProvider
				renderState={renderState}
			>
				{tileContent}
			</TileContextProvider>

			{navigationElement}
		</>
	);
};
ToDoTileContent.displayName = "ToDoTileContent";

export function useToDoTileNavigation(
	renderStateIsh: Pick<TileRenderState<AppType.ToDo>, "data" | "dimension" | "interactionState">,
	onEdit: (() => void) | undefined
): [element: ReactElement | null, () => void | Promise<void>] {
	const registerTileUsage: () => void = useRegisterTileUsage(renderStateIsh);

	const {
		settings: { destinationWindow }
	} = renderStateIsh.data;

	return useBaseNavigation(
		renderStateIsh,
		onEdit,
		useCallback(
			(): void => {
				navigateToToDoApp(
					{
						destinationWindow
					},
					() => registerTileUsage()
				);
			},
			[destinationWindow, registerTileUsage]
		)
	);
}
